import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Globe, Loader2, Search, CheckSquare, Square, Save, Check, RefreshCcw } from 'lucide-react';
import PriceDistributionView from './components/PriceDistributionView';
import {
  formatTokenCount,
  getItemExplanation,
  normalizeConfidenceScore,
  formatConfidencePercent,
  enrichPriceEstimateFields,
} from './utils/formatters';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';

// Auction / liquidation sites the website scanner is allowed to analyze.
// Keep in sync with `core/allowed_auction_sites.py` and
// `chrome-extension/background.js`.
const ALLOWED_AUCTION_DOMAINS = [
  'liquidation.com',
  'depot.auction',
  'hibid.com',
  'shopgoodwill.com',
  'estatesales.net',
  'proxibid.com',
  'mac.bid',
  'liveauctioneers.com',
  'auctionzip.com',
  'govdeals.com',
  'publicsurplus.com',
  'gsaauctions.gov',
  'allsurplus.com',
  'govplanet.com',
  'ironplanet.com',
  'rbauction.com',
  'auctiontime.com',
  'equipmentfacts.com',
  'purplewave.com',
  'bidspotter.com',
  'directliquidation.com',
  'bstock.com',
  'pristineauction.com',
  'auctionninja.com',
];

const DISALLOWED_SITE_MESSAGE = (
  'This website is not in the list of supported auction sites. ' +
  'The scanner can only analyze pages from approved auction / ' +
  'liquidation marketplaces.'
);
const MAX_CLIENT_PAGE_HTML_BYTES = 4 * 1024 * 1024;
const DEFAULT_SCAN_DELAY_MS = 0;
const MAX_SCAN_DELAY_MS = 15000;

const isAllowedAuctionUrl = (url) => {
  if (typeof url !== 'string' || !url) return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch (_err) {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }
  const host = (parsed.hostname || '').toLowerCase().replace(/^\.+|\.+$/g, '');
  if (!host) return false;
  return ALLOWED_AUCTION_DOMAINS.some(
    (domain) => host === domain || host.endsWith('.' + domain),
  );
};

const clampScanDelayMs = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return DEFAULT_SCAN_DELAY_MS;
  return Math.max(0, Math.min(MAX_SCAN_DELAY_MS, Math.round(num)));
};

const dedupeImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  return Array.from(
    new Set(
      urls
        .map((value) => String(value || '').trim())
        .filter((value) => value.startsWith('http://') || value.startsWith('https://'))
    )
  );
};

const tryFetchPageHtmlForScan = async (url) => {
  if (!url) return '';
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      redirect: 'follow',
      headers: {
        Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
      },
    });
    if (!response.ok) return '';
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (contentType && !contentType.includes('html') && !contentType.includes('xml')) {
      return '';
    }
    let html = await response.text();
    if (html.length > MAX_CLIENT_PAGE_HTML_BYTES) {
      html = html.slice(0, MAX_CLIENT_PAGE_HTML_BYTES);
    }
    return html;
  } catch (_error) {
    return '';
  }
};

const AnalyzeWebsitePage = () => {
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
    assumeHiddenItems,
    setAssumeHiddenItems,
  } = useRuntimeSettings();

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [scanError, setScanError] = useState('');
  const [analyzeError, setAnalyzeError] = useState('');
  const [scannedUrl, setScannedUrl] = useState('');
  const [scanMeta, setScanMeta] = useState(null);
  const [images, setImages] = useState([]);
  const [textSnippets, setTextSnippets] = useState([]);
  const [selectedTexts, setSelectedTexts] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [scanDelayMs, setScanDelayMs] = useState(DEFAULT_SCAN_DELAY_MS);
  const [result, setResult] = useState(null);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalNames, setOriginalNames] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);

  const allSelected = useMemo(
    () => images.length > 0 && selectedUrls.length === images.length,
    [images.length, selectedUrls.length],
  );

  const toggleSelected = (url) => {
    setSelectedUrls((prev) => (
      prev.includes(url) ? prev.filter((x) => x !== url) : [...prev, url]
    ));
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUrls([]);
    } else {
      setSelectedUrls(dedupeImageUrls(images.map((item) => item.url)));
    }
  };

  const allTextsSelected = useMemo(
    () => textSnippets.length > 0 && selectedTexts.length === textSnippets.length,
    [textSnippets.length, selectedTexts.length],
  );
  const selectedImageUrls = useMemo(
    () => dedupeImageUrls(selectedUrls),
    [selectedUrls],
  );
  const manifestRows = useMemo(
    () => (
      Array.isArray(textSnippets)
        ? textSnippets.filter((row) => row && (row.source === 'manifest_csv' || row.source === 'manifest_html'))
        : []
    ),
    [textSnippets],
  );
  const manifestInfo = (scanMeta && typeof scanMeta.manifest === 'object' && scanMeta.manifest) ? scanMeta.manifest : null;
  const hasManifestCandidates = Boolean(Array.isArray(manifestInfo?.candidates) && manifestInfo.candidates.length > 0);

  const toggleSelectedText = (textValue) => {
    setSelectedTexts((prev) => (
      prev.includes(textValue) ? prev.filter((x) => x !== textValue) : [...prev, textValue]
    ));
  };

  const toggleSelectAllTexts = () => {
    if (allTextsSelected) {
      setSelectedTexts([]);
    } else {
      setSelectedTexts(textSnippets.map((item) => item.text));
    }
  };

  const handleScan = async () => {
    const url = String(websiteUrl || '').trim();
    if (!url) return;
    if (!isAllowedAuctionUrl(url)) {
      setScanError(DISALLOWED_SITE_MESSAGE);
      return;
    }
    setLoadingScan(true);
    setScanError('');
    setAnalyzeError('');
    setResult(null);
    setScanMeta(null);
    setTextSnippets([]);
    setSelectedTexts([]);
    setSelectedUrls([]);

    try {
      const effectiveScanDelayMs = clampScanDelayMs(scanDelayMs);
      const pageHtml = await tryFetchPageHtmlForScan(url);
      const scanPayload = {
        url,
        model: modelName,
        scan_delay_ms: effectiveScanDelayMs,
      };
      if (pageHtml) {
        scanPayload.page_html = pageHtml;
      } else {
        // No client-side HTML (CORS-blocked on most auction sites).
        // Request the backend to render the page in a headless browser so
        // that JS-injected content (image carousels, lazy-loaded media,
        // etc.) is captured — this mirrors what the Chrome extension sees
        // via the live DOM and keeps the "Analyze Website" image count
        // in sync with the extension.
        scanPayload.force_browser_scan = true;
      }
      const response = await axios.post('/scan_website_images', scanPayload);
      const nextImages = Array.isArray(response?.data?.images) ? response.data.images : [];
      setImages(nextImages);
      setSelectedUrls(
        dedupeImageUrls(
          nextImages
            .filter((item) => item?.selected !== false)
            .map((item) => item.url),
        ),
      );
      const nextTextSnippets = Array.isArray(response?.data?.text_snippets) ? response.data.text_snippets : [];
      setTextSnippets(nextTextSnippets);
      setSelectedTexts(
        nextTextSnippets
          .filter((item) => item?.selected !== false)
          .map((item) => item.text),
      );
      setScannedUrl(response?.data?.url || url);
      setScanMeta({
        selectedCount: Number(response?.data?.selected_count || 0),
        duplicateCount: Number(response?.data?.duplicate_count || 0),
        relevanceAppliedCount: Number(response?.data?.relevance_applied_count || 0),
        textRelevanceAppliedCount: Number(response?.data?.text_relevance_applied_count || 0),
        relevanceModel: response?.data?.relevance_model || modelName,
        selectedTextCount: Number(response?.data?.selected_text_count || 0),
        textCount: Number(response?.data?.text_count || 0),
        scanDelayMs: Number(response?.data?.scan_delay_ms ?? effectiveScanDelayMs),
        manifest: response?.data?.manifest && typeof response.data.manifest === 'object' ? response.data.manifest : null,
      });
    } catch (error) {
      setScanError(error?.response?.data?.message || error?.message || 'Failed to scan website.');
      setImages([]);
      setSelectedUrls([]);
      setTextSnippets([]);
      setSelectedTexts([]);
      setScannedUrl('');
      setScanMeta(null);
    } finally {
      setLoadingScan(false);
    }
  };

  const handleAnalyze = async () => {
    const imageUrls = dedupeImageUrls(selectedUrls);
    if (imageUrls.length === 0) return;
    setLoadingAnalyze(true);
    setAnalyzeError('');
    setResult(null);
    try {
      const response = await axios.post('/analyze', {
        image_urls: imageUrls,
        website_context_text: selectedTexts,
        model: modelName,
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        use_notes_rag: useNotesRag,
        assume_hidden_items: assumeHiddenItems,
      });
      const resultData = response?.data || {};
      resultData.items = (Array.isArray(resultData.items) ? resultData.items : []).map((item) => {
        if (!item || typeof item !== 'object') return item;
        const explanation = getItemExplanation(item);
        const enrichedItem = enrichPriceEstimateFields(item);
        return {
          ...enrichedItem,
          explanation,
          margin_analysis: explanation,
          confidence_score: normalizeConfidenceScore(
            item.confidence_score ?? item.confidence ?? item.confidenceScore
          ),
        };
      });
      setResult(resultData);

      const prices = {};
      const names = {};
      (resultData.items || []).forEach((item) => {
        if (item?.id) {
          prices[item.id] = item.actual_price;
          names[item.id] = item.manual_name || '';
        }
      });
      setOriginalPrices(prices);
      setOriginalNames(names);
    } catch (error) {
      setAnalyzeError(error?.response?.data?.message || error?.message || 'Analyze failed.');
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, actual_price: value } : item)),
      };
    });
  };

  const handleManualNameChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, manual_name: value } : item)),
      };
    });
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      await axios.post('/update_price', { id, actual_price: parseFloat(price) });
      setOriginalPrices((prev) => ({ ...prev, [id]: parseFloat(price) }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save price.');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      await axios.post('/update_item_name', { id, item_name: name });
      setOriginalNames((prev) => ({ ...prev, [id]: name }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save item name.');
    } finally {
      setUpdatingNameId(null);
    }
  };

  const usage = (result && typeof result.usage === 'object') ? result.usage : {};
  const inputTokens = usage.prompt_tokens ?? usage.input_tokens ?? null;
  const outputTokens = usage.completion_tokens ?? usage.output_tokens ?? null;
  const totalTokens = usage.total_tokens ?? (
    Number.isFinite(Number(inputTokens)) && Number.isFinite(Number(outputTokens))
      ? (Number(inputTokens) + Number(outputTokens))
      : null
  );
  const items = Array.isArray(result?.items) ? result.items : [];
  const cacheHit = (
    result?.cache_hit === true ||
    String(result?.analysis_source || '').toLowerCase() === 'cache'
  );
  const analysisSourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const analysisSourceClass = cacheHit
    ? 'bg-orange-100 text-orange-700 border border-orange-200'
    : 'bg-green-100 text-green-700 border border-green-200';
  const resultUseEbayRag = (
    result?.use_ebay_rag !== undefined
      ? Boolean(result.use_ebay_rag)
      : useEbayRag
  );
  const ragRefs = Math.max(0, Number(result?.ebay_rag_result_count || 0) || 0);
  const resultUseNotesRag = (
    result?.use_notes_rag !== undefined
      ? Boolean(result.use_notes_rag)
      : useNotesRag
  );
  const notesRagRefs = Math.max(0, Number(result?.notes_rag_example_count || 0) || 0);
  const resultAssumeHiddenItems = (
    result?.assume_hidden_items !== undefined
      ? Boolean(result.assume_hidden_items)
      : assumeHiddenItems
  );

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Analyse web-site</h1>

      <div className="mb-4 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200 space-y-3">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
          <span className="text-sm font-medium text-slate-700 shrink-0">URL:</span>
          <div className="relative flex-1">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com/listing"
              className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <button
            type="button"
            onClick={handleScan}
            disabled={loadingScan || !websiteUrl.trim()}
            className={`px-3 py-1.5 rounded text-sm font-medium text-white ${loadingScan || !websiteUrl.trim() ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            title="Scan website"
          >
            {loadingScan
              ? <span className="inline-flex items-center gap-1"><Loader2 size={14} className="animate-spin" />Scanning</span>
              : <span className="inline-flex items-center gap-1"><Search size={14} />Scan</span>
            }
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 text-sm">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Model:</span>
            <select value={modelName} onChange={(e) => setModelName(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <optgroup label="GPT-5 Series (Latest)">
                <option value="gpt-5.4">gpt-5.4</option>
                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                <option value="gpt-5.2">gpt-5.2</option>
                <option value="gpt-5.1">gpt-5.1</option>
                <option value="gpt-5">gpt-5</option>
              </optgroup>
              <optgroup label="Gemini">
                <option value="gemini-flash-latest">gemini-flash-latest</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
              </optgroup>
              <optgroup label="Claude">
                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                <option value="claude-opus-4-6">claude-opus-4-6</option>
                <option value="claude-opus-4-7">claude-opus-4-7</option>
              </optgroup>
            </select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Resize:</span>
            <select value={resizeMode} onChange={(e) => setResizeMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <option value="original">Original</option>
              <option value="1600">Max 1600px</option>
              <option value="1280">Max 1280px</option>
              <option value="1024">Max 1024px</option>
              <option value="768">Max 768px</option>
              <option value="512">Max 512px</option>
              <option value="128">Max 128px</option>
            </select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Compression:</span>
            <select value={compressionMode} onChange={(e) => setCompressionMode(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium">
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-2">
            <span className="text-sm font-medium text-slate-700 mr-2 shrink-0">Load Pause (ms):</span>
            <input
              type="number"
              min="0"
              max={MAX_SCAN_DELAY_MS}
              step="100"
              value={scanDelayMs}
              onChange={(e) => {
                const next = Number(e.target.value);
                setScanDelayMs(Number.isFinite(next) ? clampScanDelayMs(next) : 0);
              }}
              className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-xs font-medium"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={useEbayRag} onChange={(e) => setUseEbayRag(e.target.checked)} />
            <span>eBay RAG</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={useNotesRag} onChange={(e) => setUseNotesRag(e.target.checked)} />
            <span>Notes RAG</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={assumeHiddenItems}
              onChange={(e) => setAssumeHiddenItems(e.target.checked)}
            />
            <span>Hidden Items</span>
          </label>
        </div>
      </div>

      {scanError ? <div className="mb-3 text-sm text-red-600">{scanError}</div> : null}
      {scannedUrl ? <div className="mb-3 text-xs text-slate-500 break-all">Scanned: {scannedUrl}</div> : null}
      {scanMeta ? (
        <div className="mb-3 text-xs text-slate-600">
          Auto-selected {scanMeta.selectedCount}/{images.length} | Duplicates: {scanMeta.duplicateCount} | LLM relevance checked: {scanMeta.relevanceAppliedCount} ({scanMeta.relevanceModel})
          {` | Text relevance checked: ${scanMeta.textRelevanceAppliedCount} | Text selected: ${scanMeta.selectedTextCount}/${scanMeta.textCount} | Delay: ${scanMeta.scanDelayMs}ms`}
        </div>
      ) : null}

      <div className="mb-4 border border-slate-200 rounded-lg">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Detected Images ({images.length})</div>
          <button type="button" onClick={toggleSelectAll} disabled={images.length === 0} className="text-sm text-blue-600 disabled:text-slate-400 inline-flex items-center gap-1">
            {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="max-h-[420px] overflow-auto p-2 space-y-2">
          {images.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">No images yet. Enter URL and scan.</div>
          ) : images.map((img, index) => (
            <label key={`${img.url}-${index}`} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUrls.includes(img.url)}
                onChange={() => toggleSelected(img.url)}
              />
              <img src={img.url} alt={img.alt || `Image ${index + 1}`} className="w-14 h-14 object-cover rounded border border-slate-200 bg-slate-100" />
              <div className="min-w-0">
                <div className="text-sm text-slate-700 truncate">{img.alt || `Image ${index + 1}`}</div>
                <div className="text-xs text-slate-500 truncate">{img.url}</div>
                <div className={`text-[11px] ${img.selected !== false ? 'text-emerald-600' : 'text-amber-700'} truncate`}>
                  {img.selection_reason || (img.selected !== false ? 'selected' : 'deselected')}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4 border border-slate-200 rounded-lg">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Relevant Text Snippets ({textSnippets.length})</div>
          <button type="button" onClick={toggleSelectAllTexts} disabled={textSnippets.length === 0} className="text-sm text-blue-600 disabled:text-slate-400 inline-flex items-center gap-1">
            {allTextsSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allTextsSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="max-h-[260px] overflow-auto p-2 space-y-2">
          {textSnippets.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">No text snippets extracted.</div>
          ) : textSnippets.map((row, index) => (
            <label key={`${row.text}-${index}`} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTexts.includes(row.text)}
                onChange={() => toggleSelectedText(row.text)}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="text-sm text-slate-700">{row.text}</div>
                <div className={`text-[11px] ${row.selected !== false ? 'text-emerald-600' : 'text-amber-700'} truncate`}>
                  {row.selection_reason || (row.selected !== false ? 'selected' : 'deselected')}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4 border border-slate-200 rounded-lg">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            Manifest
            {manifestInfo?.used ? (
              <span className="ml-2 text-xs font-normal text-slate-500">
                {String(manifestInfo.used.kind || 'manifest').toUpperCase()} · {Number(manifestInfo.row_count || manifestRows.length || 0)} rows
              </span>
            ) : null}
          </div>
          {manifestInfo?.used ? (
            <a
              href={manifestInfo.used.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline break-all max-w-[60%] truncate"
              title={manifestInfo.used.label || manifestInfo.used.url}
            >
              {manifestInfo.used.label || manifestInfo.used.url}
            </a>
          ) : (hasManifestCandidates ? (
            <a
              href={manifestInfo.candidates[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline break-all max-w-[60%] truncate"
            >
              {manifestInfo.candidates[0].url}
            </a>
          ) : (
            <span className="text-xs text-slate-500">{scanMeta ? 'No manifest link' : 'No scan yet'}</span>
          ))}
        </div>
        {manifestInfo?.error ? (
          <div className="px-3 pt-2 text-xs text-amber-700">{manifestInfo.error}</div>
        ) : null}
        <div className="max-h-[260px] overflow-auto p-2 space-y-1">
          {(() => {
            // Render the detected column names above the parsed rows when
            // the backend surfaced an explicit header (CSV/XLSX, or an
            // HTML manifest table with <th> headers). Mirrors the Chrome
            // extension's "Columns (N): col1 | col2 | …" header row.
            const manifestColumns = Array.isArray(manifestInfo?.columns)
              ? manifestInfo.columns.filter((c) => typeof c === 'string' && c.trim().length > 0)
              : [];
            const hasHeaderColumns = manifestColumns.length > 0
              && manifestInfo && manifestInfo.has_header !== false;
            if (!hasHeaderColumns) return null;
            return (
              <div className="text-xs text-slate-700 px-2 py-1 border-b border-slate-200 bg-slate-50 break-words">
                <span className="text-slate-500 mr-1">Columns ({manifestColumns.length}):</span>
                <strong>{manifestColumns.join(' | ')}</strong>
              </div>
            );
          })()}
          {manifestRows.length === 0 ? (
            <div className="text-sm text-slate-500 p-2">
              {!scanMeta
                ? 'No scan yet.'
                : (manifestInfo?.used ? 'No rows extracted.' : (hasManifestCandidates ? 'Manifest link found but not parsed.' : 'No manifest detected on this page.'))}
            </div>
          ) : manifestRows.map((row, index) => (
            <div key={`manifest-${index}`} className="text-xs text-slate-700 px-2 py-1 border-b border-slate-100 last:border-b-0 break-words">
              {row.text}
              <span className={`ml-2 text-[10px] ${row.selected !== false ? 'text-emerald-600' : 'text-amber-700'}`}>
                {row.selected !== false ? 'included' : 'excluded'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleAnalyze}
        disabled={loadingAnalyze || selectedImageUrls.length === 0}
        className={`w-full py-3 rounded-lg text-white font-semibold ${loadingAnalyze || selectedImageUrls.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
      >
        {loadingAnalyze ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Estimating...</span> : 'Estimate'}
      </button>

      {analyzeError ? <div className="mt-3 text-sm text-red-600">{analyzeError}</div> : null}

      {result ? (
        <div className="mt-6 border border-slate-200 rounded-lg p-4 bg-slate-50">
          <div className="flex flex-wrap gap-2 text-xs mb-3">
            <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">
              {result.model || modelName}
            </span>
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
              Resize: {result.resize_mode || resizeMode}
            </span>
            <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
              Compression: {result.compression_mode || compressionMode}
            </span>
            <span className="text-[10px] font-mono text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded">
              eBay RAG: {resultUseEbayRag ? `on (${ragRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
              Notes RAG: {resultUseNotesRag ? `on (${notesRagRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 px-2 py-0.5 rounded">
              Hidden Items: {resultAssumeHiddenItems ? 'on' : 'off'}
            </span>
            <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
              {result.analysis_duration_ms !== undefined && result.analysis_duration_ms !== null
                ? `${Number(result.analysis_duration_ms).toFixed(0)}ms`
                : 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
              {result.analysis_cost_display !== undefined
                ? result.analysis_cost_display
                : (result.analysis_cost !== undefined && result.analysis_cost !== null
                  ? `$${Number(result.analysis_cost).toFixed(4)}`
                  : 'N/A')}
            </span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${analysisSourceClass}`}>
              Src: {analysisSourceLabel}
            </span>
            <span className="text-[10px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
              In Tok: {formatTokenCount(inputTokens)}
            </span>
            <span className="text-[10px] font-mono text-violet-700 bg-violet-100 border border-violet-200 px-2 py-0.5 rounded">
              Out Tok: {formatTokenCount(outputTokens)}
            </span>
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
              Tot Tok: {formatTokenCount(totalTokens)}
            </span>
            <span className="text-[10px] font-mono text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
              Items: {items.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((item, index) => {
              const isDirty = item.id && originalPrices[item.id] !== undefined && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
              const isUpdating = updatingId === item.id;
              const isNameDirty = item.id && originalNames[item.id] !== undefined && originalNames[item.id] !== (item.manual_name || '');
              const isNameUpdating = updatingNameId === item.id;
              const condition = item?.condition || 'Unknown';
              const priceSource = item?.price_source || 'model_knowledge';
              const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
              const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
              const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';

              return (
                <div key={`${item.item || 'item'}-${index}`} className="bg-white rounded border border-slate-200 p-3 shadow-sm">
                  <strong className="block text-slate-700 text-base mb-1">{item.item || `Item ${index + 1}`}</strong>

                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-slate-500 text-xs">Manual Name:</span>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        placeholder="Enter custom name..."
                        className={`w-full pr-8 py-1 px-2 border rounded text-sm text-slate-700 focus:outline-none focus:ring-1 transition-all ${
                          isNameDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                        }`}
                        value={item.manual_name || ''}
                        onChange={(e) => handleManualNameChange(item.id, e.target.value)}
                      />
                      {item.id && (
                        <button
                          onClick={() => updateItemName(item.id, item.manual_name || '')}
                          disabled={!isNameDirty || isNameUpdating}
                          className={`absolute right-1 p-1 rounded transition-all ${
                            isNameUpdating ? 'text-slate-400' : isNameDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
                          }`}
                        >
                          {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isNameDirty ? <Save size={14} /> : <Check size={14} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-slate-600">
                      Estimated Value: <span className="font-bold text-green-600">{Number.isFinite(Number(item.estimated_value)) ? `$${Number(item.estimated_value).toFixed(2)}` : 'n/a'}</span>
                    </p>
                    <PriceDistributionView
                      rangeValue={item?.estimated_value_range}
                      distributionValue={item?.price_estimate_distribution}
                    />
                    <p className="text-sm text-slate-600">
                      Confidence: <span className="font-bold text-indigo-600">{formatConfidencePercent(item?.confidence_score)}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Actual Price:</span>
                      <div className="relative flex items-center">
                        <span className="absolute left-2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={item.actual_price}
                          onChange={(e) => handlePriceChange(item.id, e.target.value)}
                          className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                            isDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                          }`}
                          step="0.01"
                        />
                        {item.id && (
                          <button
                            onClick={() => updatePrice(item.id, item.actual_price)}
                            disabled={!isDirty || isUpdating}
                            className={`absolute right-1 p-1 rounded transition-all ${
                              isUpdating ? 'text-slate-400' : isDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default'
                            }`}
                          >
                            {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : isDirty ? <Save size={14} /> : <Check size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[9px] text-slate-400">Condition:</span>
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
                    <span className="text-[9px] text-slate-400 ml-1">AI Src:</span>
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
                  </div>

                  {item.explanation && (
                    <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 leading-relaxed">
                      {item.explanation}
                    </div>
                  )}
                </div>
              );
            })}
            {items.length === 0 ? <div className="text-sm text-slate-500">No items returned.</div> : null}
          </div>
          {items.length >= 1 && (
            <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg flex justify-between items-center">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
              <span className="text-base text-green-600 font-bold">
                ${items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AnalyzeWebsitePage;
